import { useMemo } from 'react';

import { gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';

export const VALUE_COUNT_READS_CONNECTION_KEY = 'contentLabelValueCounts';

export const CONTENT_LABEL_VALUE_COUNTS_QUERY = gql`
  query ContentLabelValueCounts($teamId: Int!, $portfolioId: Float!) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${VALUE_COUNT_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      values {
        contentLabelCategoryValueId
        contentLabelCategoryId
        contentCount(portfolioId: $portfolioId)
      }
    }
  }
`;

type ValueCountRow = {
  contentLabelCategoryValueId: string;
  contentLabelCategoryId: string;
  contentCount: number;
};

export type ContentLabelValueCountsResponse = {
  listContentLabelCategories: { contentLabelCategoryId: string; values?: ValueCountRow[] | null }[];
};

export type ContentLabelValueCountsVariables = { teamId: number; portfolioId: number };

const NO_COUNTS: ReadonlyMap<string, number> = new Map();

/**
 * Counts by value id. An absent id means "not counted", which callers must keep
 * distinct from a counted zero — the drawer hides a 0 but never an unknown.
 */
export function useContentLabelValueCounts({ portfolioId, skip }: { portfolioId: number; skip?: boolean }): {
  counts: ReadonlyMap<string, number>;
  loading: boolean;
} {
  const { teamId } = useTeam();
  const { data, loading } = useQuery<ContentLabelValueCountsResponse, ContentLabelValueCountsVariables>(
    CONTENT_LABEL_VALUE_COUNTS_QUERY,
    // Label writes in Settings never reach this read, so every mount asks again.
    { variables: { teamId, portfolioId }, skip: skip || !portfolioId, fetchPolicy: 'cache-and-network' },
  );

  // Memoised: the catalog below it is a `useMemo` keyed on this map, so a fresh
  // Map per render would rebuild every catalog on every render.
  const counts = useMemo<ReadonlyMap<string, number>>(() => {
    const rows = data?.listContentLabelCategories;
    if (!rows?.length) return NO_COUNTS;
    const next = new Map<string, number>();
    rows.forEach((category) =>
      (category?.values ?? []).forEach((value) => {
        if (value && typeof value.contentCount === 'number') {
          next.set(value.contentLabelCategoryValueId, value.contentCount);
        }
      }),
    );
    return next.size ? next : NO_COUNTS;
  }, [data]);

  return { counts, loading: Boolean(loading) && !data };
}
